import { type ComponentProps, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import listIcon from '../../assets/sidebar/List.svg';
import logoutIcon from '../../assets/sidebar/Logout.svg';
import BrandLogo from '../BrandLogo';
import { useEventContext } from '../../contexts/EventContext';
import { EVENT_LIST_ROUTE_PATH, LOGIN_ROUTE_PATH } from '../../routes/appRoutes';
import { changePassword, logout } from '../../services/authService';
import { eventService } from '../../services/eventService';
import {
  getStoredUserDisplayName,
  loadMyManagerProfile,
  loadUserDisplayName,
} from '../../services/managerService';
import { formatPhoneNumberForDisplay } from '../../utils/phoneNumber';
import type { ManagerProfileResponse } from '../../api/managerApi';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/** 'YYYY-MM-DD' → 'YYYY-MM-DD (요일)' */
function formatDateKo(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${value} (${WEEKDAYS[date.getDay()]})`;
}

type HeaderProps = Readonly<{
  /** 대회 선택 전 화면에서는 대회명과 메뉴 버튼을 숨김 */
  standalone?: boolean;
}>;

const roleLabelByValue: Record<string, string> = {
  SUPER: '슈퍼 관리자',
  MIDDLE: '중간 관리자',
  GENERAL: '일반 관리자',
  NORMAL: '일반 사용자',
};

function getRoleLabel(role?: string) {
  if (!role) {
    return '-';
  }

  return roleLabelByValue[role] ?? role;
}

function validatePasswordPolicy(password: string) {
  if (password.length < 8) {
    return '새 비밀번호는 8자 이상 입력해주세요.';
  }

  if (!/[A-Za-z]/.test(password) || !/\d/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
    return '새 비밀번호는 영문, 숫자, 특수문자를 모두 포함해야 합니다.';
  }

  return '';
}

type PasswordChangeSuccessProps = Readonly<{
  onConfirm: () => void;
}>;

function PasswordChangeSuccess({ onConfirm }: PasswordChangeSuccessProps) {
  return (
    <div className="password-change-success">
      <div className="password-change-title">
        <span className="password-change-gear" aria-hidden="true" />
        <h2>비밀번호 변경</h2>
      </div>
      <p className="password-change-success-message">비밀번호 변경이 성공하였습니다.</p>
      <button className="password-change-save" type="button" onClick={onConfirm}>
        로그인 화면으로
      </button>
    </div>
  );
}

function getProfileModalView(isPasswordChangeMode: boolean, isPasswordChanged: boolean) {
  if (isPasswordChanged) {
    return 'password-success';
  }

  if (isPasswordChangeMode) {
    return 'password-form';
  }

  return 'profile';
}

function Header({ standalone = false }: HeaderProps) {
  const navigate = useNavigate();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPasswordChangeMode, setIsPasswordChangeMode] = useState(false);
  const [displayName, setDisplayName] = useState(() => getStoredUserDisplayName() ?? '사용자');
  const [profile, setProfile] = useState<ManagerProfileResponse | null>(null);
  const [profileErrorMessage, setProfileErrorMessage] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [isPasswordSaving, setIsPasswordSaving] = useState(false);
  const [isPasswordChanged, setIsPasswordChanged] = useState(false);
  const { selectedEventId, selectedEventName } = useEventContext();
  const [eventDate, setEventDate] = useState<string | null>(null);
  const profileModalView = getProfileModalView(isPasswordChangeMode, isPasswordChanged);

  useEffect(() => {
    let isMounted = true;

    loadUserDisplayName()
      .then((name) => {
        if (isMounted) {
          setDisplayName(name);
        }
      })
      .catch(() => {
        if (isMounted) {
          setDisplayName(getStoredUserDisplayName() ?? '사용자');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (selectedEventId == null) {
      setEventDate(null);
      return;
    }

    let isMounted = true;

    eventService
      .detail(selectedEventId)
      .then((detail) => {
        if (isMounted) {
          setEventDate(detail.eventDate);
        }
      })
      .catch(() => {
        if (isMounted) {
          setEventDate(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedEventId]);

  const handleLogout = async () => {
    await logout();
    navigate(LOGIN_ROUTE_PATH, { replace: true });
  };

  const closeProfileModal = () => {
    if (isPasswordChanged) {
      navigate(LOGIN_ROUTE_PATH, { replace: true });
      return;
    }

    setIsProfileModalOpen(false);
    setIsPasswordChangeMode(false);
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordConfirm('');
    setPasswordErrorMessage('');
    setIsPasswordChanged(false);
  };

  const openProfileModal = () => {
    setIsProfileModalOpen(true);
    setProfileErrorMessage('');
    loadMyManagerProfile()
      .then((nextProfile) => {
        setProfile(nextProfile);
        setDisplayName(nextProfile.name ?? nextProfile.account);
      })
      .catch(() => {
        setProfileErrorMessage('사용자 정보를 불러올 수 없습니다.');
      });
  };

  const openPasswordChangeForm = () => {
    setIsPasswordChangeMode(true);
    setPasswordErrorMessage('');
    setIsPasswordChanged(false);
  };

  const returnToProfile = () => {
    setIsPasswordChangeMode(false);
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordConfirm('');
    setPasswordErrorMessage('');
    setIsPasswordChanged(false);
  };

  const confirmPasswordChanged = () => {
    navigate(LOGIN_ROUTE_PATH, { replace: true });
  };

  const submitPasswordChange = async () => {
    setPasswordErrorMessage('');

    if (!currentPassword || !newPassword || !newPasswordConfirm) {
      setPasswordErrorMessage('비밀번호를 모두 입력해주세요.');
      return;
    }

    const passwordPolicyMessage = validatePasswordPolicy(newPassword);

    if (passwordPolicyMessage) {
      setPasswordErrorMessage(passwordPolicyMessage);
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setPasswordErrorMessage('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
      return;
    }

    setIsPasswordSaving(true);
    try {
      await changePassword(currentPassword, newPassword);
      logout();
      setIsPasswordChanged(true);
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch {
      setPasswordErrorMessage('비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setIsPasswordSaving(false);
    }
  };

  const handlePasswordChangeSubmit: ComponentProps<'form'>['onSubmit'] = (event) => {
    event.preventDefault();
    void submitPasswordChange();
  };

  return (
    <>
      <header className="topbar">
        <div className="topbar-left">
          <BrandLogo />
          {!standalone && (
            <>
              <div className="event-title">{selectedEventName ?? '대회를 선택하세요'}</div>
              <Link
                className="menu-button"
                to={EVENT_LIST_ROUTE_PATH}
                aria-label="대회 목록으로 이동"
              >
                <img className="menu-button-icon" src={listIcon} alt="" aria-hidden="true" />
              </Link>
            </>
          )}
        </div>
        <div className="topbar-right">
          {!standalone && selectedEventId != null && eventDate && (
            <span className="event-date">대회일자: {formatDateKo(eventDate)}</span>
          )}
          <div className="profile">
            <button
              className="avatar"
              type="button"
              aria-label="프로필 메뉴 열기"
              onClick={openProfileModal}
            >
              {displayName.slice(0, 1)}
            </button>
            <span className="profile-name">{displayName}</span>
            <button className="logout-button" type="button" aria-label="로그아웃" onClick={handleLogout}>
              <img className="logout-icon" src={logoutIcon} alt="" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {isProfileModalOpen && (
        <dialog
          className="modal-backdrop"
          open
          aria-label="프로필 모달"
          onCancel={closeProfileModal}
        >
          <button
            className="modal-backdrop-button"
            type="button"
            aria-label="프로필 모달 닫기"
            onClick={closeProfileModal}
          />
          <div className="profile-modal">
            <button
              className="modal-close-button"
              type="button"
              aria-label="프로필 모달 닫기"
              onClick={closeProfileModal}
            />
            {profileModalView === 'password-success' && (
              <PasswordChangeSuccess onConfirm={confirmPasswordChanged} />
            )}
            {profileModalView === 'password-form' && (
              <form className="password-change-form" onSubmit={handlePasswordChangeSubmit}>
                <div className="password-change-title">
                  <span className="password-change-gear" aria-hidden="true" />
                  <h2>비밀번호 변경</h2>
                </div>
                <label className="password-change-field">
                  <span>현재 비밀번호</span>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    placeholder="현재 비밀번호"
                    autoComplete="current-password"
                  />
                </label>
                <label className="password-change-field">
                  <span>새 비밀번호</span>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder="새 비밀번호"
                    autoComplete="new-password"
                  />
                  <small>*영문자+숫자+특수문자 포함, 8자 이상</small>
                </label>
                <label className="password-change-field">
                  <span>새 비밀번호 확인</span>
                  <input
                    type="password"
                    value={newPasswordConfirm}
                    onChange={(event) => setNewPasswordConfirm(event.target.value)}
                    placeholder="새 비밀번호 확인"
                    autoComplete="new-password"
                  />
                  <small>*비밀번호를 다시 한번 입력해주세요.</small>
                </label>
                {passwordErrorMessage && <p className="profile-modal-error">{passwordErrorMessage}</p>}
                <div className="password-change-actions">
                  <button className="password-change-cancel" type="button" onClick={returnToProfile}>
                    취소
                  </button>
                  <button
                    className="password-change-save"
                    type="submit"
                    disabled={isPasswordSaving}
                  >
                    {isPasswordSaving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </form>
            )}
            {profileModalView === 'profile' && (
              <div className="profile-modal-content">
                <div className="profile-modal-summary">
                  <div className="profile-modal-avatar" aria-hidden="true">
                    {(profile?.name ?? profile?.account ?? displayName).slice(0, 1)}
                  </div>
                  <div className="profile-modal-heading">
                    <h2>{profile?.name ?? profile?.account ?? displayName}</h2>
                    <span>{getRoleLabel(profile?.role)}</span>
                  </div>
                </div>
                <div className="profile-info-list">
                  <div className="profile-info-row">
                    <span className="profile-info-label">이름:</span>
                    <span className="profile-info-value">{profile?.name ?? profile?.account ?? displayName}</span>
                  </div>
                  <div className="profile-info-row">
                    <span className="profile-info-label">권한:</span>
                    <span className="profile-info-value">{getRoleLabel(profile?.role)}</span>
                  </div>
                  <div className="profile-info-row">
                    <span className="profile-info-label">전화번호:</span>
                    <span className="profile-info-value">
                      {formatPhoneNumberForDisplay(profile?.contactNumber)}
                    </span>
                  </div>
                  <div className="profile-info-row">
                    <span className="profile-info-label">이메일:</span>
                    <span className="profile-info-value">{profile?.email ?? '-'}</span>
                  </div>
                </div>
                {profileErrorMessage && <p className="profile-modal-error">{profileErrorMessage}</p>}
                <button
                  className="profile-password-button"
                  type="button"
                  onClick={openPasswordChangeForm}
                >
                  비밀번호 변경
                </button>
              </div>
            )}
          </div>
        </dialog>
      )}
    </>
  );
}

export default Header;
